import React, { useEffect, useRef } from 'react';
import './styles/MobileVerification.css';
import { AutoComplete, Button, Card, Flex, Input, Result, Typography } from 'antd';
import { numberValidation } from '../utils/Validation';
import { useNavigate } from 'react-router-dom';
import countries from 'world-countries';
import ToastMsg from '../components/common/ToastMsg';
import { useAppDispatch, useAppSelector } from '../hooks/useTypedSelector';
import smsIcon from '../assets/icons/smsIcon.svg';
import whatsappIcon from '../assets/icons/whatsappIcon.svg';

import {
    setSelectedCountry,
    setCountryField,
    setMobileNumber,
    setSmsTriggered,
    setDisableResendOtp,
    setResendTimer,
    setError,
    setOtp,
    setResendAttempt,
    setVerifyAttempt,
    setOtpVerified,
    setOptions,
    setDisableBtn,
    resetMobileStoreState,
    setLastSentType,
    setPhoneExistsError,
    setotpError,
    setMobileUnverifiedUser,
    setVerificationStatusLoaded,
    setisModalOpen
} from '../redux/mobileVerificationSlice';
import API from '../services/axiosInterceptor';
import backgrounImage from '../assets/images/backgroundOnboard.svg';
import dropdownIcon from '../assets/icons/dropdownBlue.svg';

const seenDialCodes = new Set();
const countryOptions = countries
    .filter(country => country.idd?.root)
    .map(country => {
        const dialCode = `${country.idd.root}${(country.idd.suffixes && country.name.common !== "United States" && country.name.common !== "Canada") ? country.idd.suffixes[0] : ""}`
        return {
            label: `${country.name.common}: ${dialCode || ""}`,
            isoCode: country.cca2,
            value: dialCode,
            searchKey: `${country.name.common.toLowerCase()} ${dialCode}`
        };
    })
    .filter(option => {
        // Ensure unique dial codes (remove duplicates)
        if (!seenDialCodes.has(option.value) || option.value === "+1") {
            seenDialCodes.add(option.value);
            return true;
        }
        return false;
    });


// OTPInputs component extracted to reduce complexity
const OTPInputs: React.FC<{
    otp: string[];
    otpError: string;
    error: boolean;
    resendAttempt: number;
    disableResendOtp: boolean;
    resendTimer: number;
    verifyAttempt: number;
    disableBtn: boolean;
    handleOtpChange: (value: string) => void;
    handleOtpKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    handleBack: () => void;
    handleVerify: () => void;
    resendOtp: () => void;
}> = ({
    otp,
    otpError,
    error,
    resendAttempt,
    disableResendOtp,
    resendTimer,
    verifyAttempt,
    disableBtn,
    handleOtpChange,
    handleOtpKeyDown,
    handleBack,
    handleVerify,
    resendOtp
}) => {
        const { Text } = Typography;
        const isOtpComplete = otp.join("").length === 6;
        return (
            <div className="otp-container">
                <div className="EnterOtpTextContainer">
                    <Text className="EnterOtpText">Enter OTP</Text>
                </div>
                <div className="otp-dabba" style={{ display: 'flex', gap: '10px', margin: '10px 0' }}>
                    <Input
                        className={`otpInput ${otpError ? "otp-input-error" : "otp-input-no-error"}`}
                        maxLength={6}
                        value={otp.join("")}
                        onChange={(e) => handleOtpChange(e.target.value)}
                        onKeyDown={handleOtpKeyDown}
                        style={{
                            width: '200px',
                            height: '40px',
                            textAlign: 'center',
                            fontSize: '18px',
                            letterSpacing: '5px'
                        }}
                        type="tel"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                    />
                </div>
                {error && <Text style={{ color: "red" }}>Please enter valid OTP</Text>}
                <Text
                    style={{ color: (resendAttempt > 3 || disableResendOtp) ? "#6b8196" : "#1890ff", cursor: "pointer", textAlign: 'right' }}
                    onClick={resendOtp}
                >
                    {disableResendOtp
                        ? `Resend OTP in ${resendTimer} seconds`
                        : "Resend OTP"}
                </Text>
                <Flex className="backVerifyBtns" gap={'1vw'}>
                    <Button
                        className="backBtnOtp"
                        type="default"
                        style={{ width: '50%' }}
                        onClick={handleBack}
                    >Back</Button>
                    <Button
                        type="primary"
                        className="verifyBtnOtp"
                        style={{ width: '50%' }}
                        block
                        disabled={verifyAttempt > 3 || disableBtn || !isOtpComplete}
                        onClick={handleVerify}
                    >
                        Verify
                    </Button>
                </Flex>
            </div>
        );
    };

const MobileVerification: React.FC<{ isModal?: boolean; modalPurpose?: string; onVerified?: () => void }> = ({ isModal = false, modalPurpose, onVerified }) => {
    const { Text, Title } = Typography;
    const { lastSentType, phoneExistsError, otpError } = useAppSelector((state) => state.mobileVerificationData);
    const state = useAppSelector((state) => state.mobileVerificationData);
    const {
        selectedCountry,
        countryField,
        mobileNumber,
        smsTriggered,
        disableResendOtp,
        resendTimer,
        error,
        otp,
        resendAttempt,
        verifyAttempt,
        otpVerified,
        options,
        disableBtn,
    } = state
    const phoneVerified = useAppSelector((state) => state.auth)
    const dispatch = useAppDispatch()
    const navigate = useNavigate();

    useEffect(() => {
        const handleBackButton = () => {
            dispatch(resetMobileStoreState());
        };
        window.addEventListener("popstate", handleBackButton);
        return () => {
            window.removeEventListener("popstate", handleBackButton);
        };
    }, [dispatch]);

    const onChangeInput = (e: any) => {
        let value = e.target.value;
        if (value === "" || numberValidation(value)) {
            dispatch(setPhoneExistsError(false))
            dispatch(setMobileNumber(value))
        }
    };

    const handleSelect = (value: string) => {
        dispatch(setSelectedCountry(value))
        dispatch(setCountryField(value))
        dispatch(setOptions(countryOptions))
    };

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (disableResendOtp && resendTimer > 0) {
            timer = setInterval(() => {
                dispatch(setResendTimer(resendTimer - 1));
            }, 1000);
        } else if (resendTimer === 0) {
            dispatch(setDisableResendOtp(false))
        }
        return () => clearInterval(timer);
    }, [disableResendOtp, resendTimer, dispatch]);

    const smsTigger = async (from: string) => {
        try {
            dispatch(setDisableBtn(true))
            const dialCode = (selectedCountry || '').replace(/^\+/, '');
            let data = {
                isoCode: options.find((item) => item.value === selectedCountry)?.isoCode ?? 'IN',
                recipient: `${dialCode}${mobileNumber}`,
                messageType: from
            }
            const response = await API.post(`auth/send-otp`, data);
            if (response.data.message) {
                dispatch(setSmsTriggered(true))
                dispatch(setDisableResendOtp(true))
                dispatch(setResendTimer(30))
                dispatch(setDisableBtn(false))
            }
        } catch (error: any) {
            dispatch(setDisableBtn(false));

            const errorMsg = error.response?.data?.message ?? error.message;
            let toastAlreadyShown = false;

            if (errorMsg?.toLowerCase().includes("phone number already exist")) {
                dispatch(setPhoneExistsError(true));
                console.error("Phone number already exists:", errorMsg);
                if (error.response?.status !== 404 && error.response?.status !== 500) {
                    ToastMsg("error", errorMsg);
                }
                toastAlreadyShown = true;
            } else {
                dispatch(setPhoneExistsError(false));
            }

            if (errorMsg?.toLowerCase().includes("incorrect otp") || errorMsg?.toLowerCase().includes("otp has expired")) {
                dispatch(setotpError(errorMsg));
                console.error("Incorrect OTP or OTP has expired:", errorMsg);
                if (!toastAlreadyShown) {
                    if (error.response?.status !== 404 && error.response?.status !== 500) {
                        ToastMsg("error", errorMsg);
                    }
                    toastAlreadyShown = true;
                }
            } else {
                dispatch(setotpError(""));
            }

            if (!toastAlreadyShown) {
                console.error("General error:", errorMsg);
                if (error.response?.status !== 404 && error.response?.status !== 500) {
                    ToastMsg("error", errorMsg);
                }
            }
        }


    };

    const verifyOtp = async () => {
        try {
            dispatch(setDisableBtn(true));
            const dialCode = (selectedCountry || '').replace(/^\+/, '');
            let data = {
                isoCode: options.find((item) => item.value === selectedCountry)?.isoCode ?? 'IN',
                recipient: `${dialCode}${mobileNumber}`,
                otp: Number(otp.join(""))
            };
            const response = await API.post(`auth/verify-otp`, data);
                if (response.data.message) {
                dispatch(setOtpVerified(true));

                dispatch(setMobileUnverifiedUser(false));
                dispatch(setVerificationStatusLoaded(true));
                dispatch(setisModalOpen(false));

                sessionStorage.setItem('mobile_verification_completed', 'true');

                try { sessionStorage.removeItem('mobile_verify_request'); } catch (e) { console.error("Error removing mobile_verify_request:", e)}
                try { sessionStorage.removeItem('mobile_verify_redirect'); } catch (e) { console.error("Error removing mobile_verify_redirect:", e)}

                setTimeout(() => {
                  if (onVerified) onVerified();
                }, 100);

                const redirectPath = sessionStorage.getItem("mobile_verify_redirect");

                setTimeout(() => {
                    if (isModal) {
                        if (redirectPath) sessionStorage.removeItem("mobile_verify_redirect");
                        return;
                    }
                    else {
                        navigate("/onboard");
                    }
                }, 1000);

                dispatch(setDisableBtn(false));
            }


        } catch (error: any) {
            dispatch(setVerifyAttempt(verifyAttempt + 1));
            dispatch(setDisableBtn(false));
            const errorMsg = error.response?.data?.message ?? error.message;

            if (errorMsg?.toLowerCase().includes("incorrect otp") || errorMsg.toLowerCase().includes("otp has expired")) {
                dispatch(setotpError(errorMsg));
                console.error('Incorrect OTP or OTP has expired:', errorMsg);
            } else {
                dispatch(setotpError(""));
            }

            if (errorMsg && !errorMsg.toLowerCase().includes("phone number not verified")) {
                console.error('Error verifying OTP:', errorMsg);
                if (error.response?.status !== 404 && error.response?.status !== 500) {
                    ToastMsg("error", errorMsg);
                }
            }
        }
    };

    useEffect(() => {
        if (phoneVerified?.phoneVerified) {
            dispatch(setOtpVerified(true));
            setTimeout(() => {
                navigate("/onboard");
            }, 1000);
        }
    }, [phoneVerified, dispatch, navigate]);


    const handleBack = () => {
        dispatch(setOtp(new Array(6).fill('')))
        dispatch(setResendAttempt(0));
        dispatch(setVerifyAttempt(0));
        dispatch(setSmsTriggered(false))
    }


    const handleOtpChange = (value: string) => {
        if (!numberValidation(value)) return;
        if (error) dispatch(setError(false));
        const otpArray = value.split("");
        dispatch(setOtp(otpArray));
        // If OTP is cleared, also clear otpError so input class changes to no-error
        if (value.length < 6) {
            dispatch(setotpError(""));
        }
    };
    const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const isOtpComplete = otp.join("").length === 6;
            if (isOtpComplete && !disableBtn && verifyAttempt <= 3) {
                handleVerify();
            }
        }
    };

    const handleVerify = () => {
        if (!otp || otp.some((digit) => !digit)) {
            dispatch(setError(true));
            return;
        }
        verifyOtp();
    };

    const resendOtp = () => {
        if (!disableResendOtp && resendAttempt <= 3) {
            smsTigger(lastSentType)
            dispatch(setResendAttempt(resendAttempt + 1))
        }
    }

    const handleSearch = (value: string) => {
        dispatch(setCountryField(value))
        const filteredOptions = countryOptions.filter((option) =>
            option.searchKey.includes(value.toLowerCase()) // Search by name or dial code
        );
        dispatch(setOptions(JSON.parse(JSON.stringify(filteredOptions))))
    };

    const handleBlur = () => {
        dispatch(setCountryField(selectedCountry))
        dispatch(setOptions(countryOptions))
    }


    const mobileInputRef = useRef<any>(null);

    return (
        <div
            className="inner-page-main"
            style={{
                backgroundImage: !isModal ? `url(${backgrounImage})` : 'none',
                background: isModal ? "none" : `url(${backgrounImage})`,
                display: 'flex',
                alignItems: "center",
                flexDirection: isModal ? "column" : "row",
                justifyContent: "center",
                gap: isModal ? "50px" : "initial"
            }}
        >
            {isModal && (
                <div className="modalText" style={{ fontSize: "22px", fontWeight: "500", textAlign: "center" }}>
                    {modalPurpose ? `Please verify your mobile to ${modalPurpose}` : 'Verify your number to unlock 5,000+ universities, AI matching, and comparison tools'}
                </div>
            )}
            <div className="outer-wrapper">
                {otpVerified ?
                    <Card className="successCard">
                        <Result
                            className="result"
                            status="success"
                            title="Verified Successfully"
                        />
                    </Card>
                    : <div className="container">
                        <Card className={`mobile-verification-card${smsTriggered ? ' otp-mode' : ''}`} bordered={false}>

                            <div className="headingContainer">
                                <Title level={5} className="heading-font">Unlock free access</Title>
                                <Text className="mobileUse">What unlocks: all universities · AI match score · 50+ filters · comparison · expert calls</Text>
                                <Text className="mobileUse" style={{ fontSize: '12px', color: '#999' }}>Your number is used only to verify you are a real user. No marketing. No ongoing messages.</Text>
                            </div>
                            <div className="input-container" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <AutoComplete
                                    disabled={smsTriggered}
                                    className={`countryCodeInput ${phoneExistsError ? "input-error" : ""}`} value={countryField}
                                    onSelect={(value) => {
                                        handleSelect(value);
                                        setTimeout(() => {
                                            mobileInputRef.current?.focus();
                                        }, 0);
                                    }}
                                    options={options}
                                    suffixIcon={<img src={dropdownIcon} style={{ color: '#4A709C', fontSize: '16px' }} alt="dropdown icon" />}
                                    popupMatchSelectWidth={200}
                                    onSearch={handleSearch}
                                    onFocus={() => dispatch(setCountryField(""))}
                                    onBlur={handleBlur}
                                    bordered={false}
                                />

                                <Input
                                    ref={mobileInputRef}
                                    disabled={smsTriggered}
                                    className={`mobileNumberInput ${phoneExistsError ? "input-error" : ""}`}
                                    value={mobileNumber}
                                    onChange={(e) => onChangeInput(e)}
                                    placeholder="Mobile Number"
                                    style={{ fontSize: '16px', fontWeight: 500 }}
                                    bordered={false}
                                    maxLength={selectedCountry === "+91" ? 10 : 17}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            if (
                                                !disableBtn &&
                                                selectedCountry === "+91" &&
                                                mobileNumber?.length === 10
                                            ) {
                                                dispatch(setLastSentType("whatsapp"));
                                                smsTigger("whatsapp");
                                            }
                                        }
                                    }}
                                />

                            </div>
                            {smsTriggered ? (
                                <OTPInputs
                                    otp={otp}
                                    otpError={typeof otpError === "string" ? otpError : ""}
                                    error={error}
                                    resendAttempt={resendAttempt}
                                    disableResendOtp={disableResendOtp}
                                    resendTimer={resendTimer}
                                    verifyAttempt={verifyAttempt}
                                    disableBtn={disableBtn}
                                    handleOtpChange={handleOtpChange}
                                    handleOtpKeyDown={handleOtpKeyDown}
                                    handleBack={handleBack}
                                    handleVerify={handleVerify}
                                    resendOtp={resendOtp}
                                />
                            ) : (
                                <div>
                                    <div className="OtpSend">
                                        <Title level={5} style={{ margin: "0" }} className='heading-font'>Verify your number:</Title>
                                        <Flex gap={'1vw'}>
                                            {countryField === "+91" && (
                                                <Button
                                                    className="verificationMethodTypesSMS"
                                                    type="default"
                                                    disabled={disableBtn || !mobileNumber || mobileNumber?.length < 10}
                                                    onClick={() => {
                                                        dispatch(setLastSentType("sms"));
                                                        smsTigger("sms")
                                                    }}
                                                >
                                                    <img src={smsIcon} alt="SMS icon" style={{ color: "#d9d9d9" }} />
                                                    SMS
                                                </Button>
                                            )}
                                            <Button className="verificationMethodTypesWhatsapp" type="primary" onClick={() => { dispatch(setLastSentType("whatsapp")); smsTigger("whatsapp") }} disabled={disableBtn || !mobileNumber || (countryField === "+91" && mobileNumber?.length < 10)}>
                                                <img src={whatsappIcon} alt="whatsapp" />
                                                Whatsapp
                                            </Button>
                                        </Flex>
                                    </div>
                                    {!isModal && (
                                        <div className="skipMobileVerificationContainer">
                                            <Text className="skipMobileVerificationText" onClick={() => navigate('/search')}>Skip for now</Text>
                                        </div>
                                    )}
                                </div>

                            )}
                        </Card>
                    </div>
                }
            </div>
        </div>
    );
};

export default MobileVerification;